import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Archive,
  BarChart3,
  List,
  Upload,
  Users,
  Users2,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  Pencil,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { uiBtn } from '../ui/buttonClasses';
import { coachStorageKey, COACH_STORAGE_SUFFIX } from '../../utils/coachStorage';
import { useDashboards } from '../../hooks/useDashboardData';
import { useDashboardBuilderDrafts } from '../../hooks/useDashboardBuilderDrafts';
import { draftNavigationSubItem } from '../coach/dashboard-builder/dashboardBuilderDraftStorage';
import {
  useSessionsList,
  useRecentRowers,
  useRecentTeams,
  useRecentSessionIds,
} from '../../hooks/useSessionData';
import { useCoachPreferences } from '../../hooks/useCoachPreferences';
import { useCoachTeamNavContext } from '../../hooks/useCoachTeamNavContext';
import { isPrototypesEnabled } from '../../config/debug';
import { getPrototypesForPortal } from '../../features/prototypes/prototypeRegistry';
import { useProfileRoles } from '../../hooks/useProfileRoles';
import { UserRole } from '../../types/database';
import Sidebar from './Sidebar';
import SidebarDrillTransition from './SidebarDrillTransition';
import SidebarUtilityStrip from './SidebarUtilityStrip';
import SidebarProfileMenu from './SidebarProfileMenu';
import SessionsArchiveDialog from '../coach/sessions/SessionsArchiveDialog';
import { getArchiveDialogDetailRows } from '../coach/sessions/archiveSessionSummary';
import { profileService } from '../../services/users/profileService';
import { useSidebarDrill } from '../../contexts/SidebarDrillContext';

interface CoachSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  activeTab: string;
  onTabChange: (tab: string, subItem?: string) => void;
  currentDashboardId?: string | null;
}

type PrototypeNavItem = {
  id: string;
  title: string;
  platform: 'D3.js' | 'Three.js' | 'Other';
};

/** Sub-views that should re-open the matching sidebar drill when the user navigates to them. */
function sidebarDrillPanelForLocation(pathname: string, search: string): string | null {
  if (/^\/coach\/teams\/[^/]+$/.test(pathname)) return 'teams';
  if (/^\/coach\/rower\/[^/]+\/(journey|profile)\/?$/.test(pathname)) return 'roster';
  if (pathname.startsWith('/coach/prototypes/') && pathname !== '/coach/prototypes') {
    return 'prototypes';
  }
  if (/^\/coach\/dashboard\/[^/]+$/.test(pathname)) return 'dashboard';
  if (pathname === '/coach/upload') return 'sessions';

  if (pathname === '/coach' || pathname === '/coach/') {
    const params = new URLSearchParams(search);
    const mode = params.get('mode');
    if (mode === 'custom' && params.get('dashboard')) return 'dashboard';
    if (mode === 'builder') return 'dashboard';
  }

  return null;
}

const CoachSidebar: React.FC<CoachSidebarProps> = ({
  isCollapsed,
  onToggle,
  activeTab,
  onTabChange,
  currentDashboardId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarDrill = useSidebarDrill();
  const { user, signOut } = useAuth();
  const userId = user?.id;
  const selectedSessionStorageKey = userId
    ? coachStorageKey(userId, COACH_STORAGE_SUFFIX.selectedSessionId)
    : null;
  const { data: dashboards = [] } = useDashboards();
  const builderDrafts = useDashboardBuilderDrafts();
  const {
    data: sessions = [],
    archiveSessions,
    isArchiving,
    archiveError,
    setArchiveError,
  } = useSessionsList();
  const { data: allRecentRowers = [] } = useRecentRowers(3);
  const { data: allRecentTeams = [] } = useRecentTeams(3);
  const { data: recentSessionIds = [] } = useRecentSessionIds(10);
  const { preferences: coachPreferences } = useCoachPreferences();
  const teamNav = useCoachTeamNavContext();
  const { isDeveloper } = useProfileRoles();
  const showPrototypes = isPrototypesEnabled() && isDeveloper;

  const recentRowers = allRecentRowers.filter(
    (rower) => coachPreferences.showDemoContent || !rower.isDemo
  );
  const recentTeams = allRecentTeams.filter(
    (team) => coachPreferences.showDemoContent || !team.isDemo
  );
  const activePrototypeId = location.pathname.match(/^\/coach\/prototypes\/([^/]+)$/)?.[1] ?? null;

  const [activeDrillPanel, setActiveDrillPanel] = useState<string | null>(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [profileFullName, setProfileFullName] = useState<string | null>(null);

  const dashboardSessionId = useMemo(() => {
    const m = location.pathname.match(/\/coach\/dashboard\/([^/]+)/);
    if (m?.[1]) return m[1];
    const q = new URLSearchParams(location.search).get('session');
    if (q) return q;
    if (location.pathname === '/coach' || location.pathname === '/coach/') {
      return selectedSessionStorageKey ? sessionStorage.getItem(selectedSessionStorageKey) : null;
    }
    return null;
  }, [location.pathname, location.search, selectedSessionStorageKey]);

  const archiveSessionForDialog = useMemo(
    () => (dashboardSessionId ? sessions.find((s) => s.id === dashboardSessionId) : undefined),
    [sessions, dashboardSessionId]
  );

  const archiveTargetName = archiveSessionForDialog?.sessionName?.trim() || 'Session';

  useEffect(() => {
    const loadProfileName = async () => {
      if (!user?.id) {
        setProfileFullName(null);
        return;
      }

      const profile = await profileService.getProfile(user.id);
      setProfileFullName(profile?.full_name?.trim() || null);
    };

    loadProfileName();
  }, [user?.id]);

  const archiveDetailRows = useMemo(() => {
    if (!dashboardSessionId) return [];
    if (archiveSessionForDialog) {
      return getArchiveDialogDetailRows(archiveSessionForDialog);
    }
    return [
      {
        label: 'Tip',
        value: 'Open Sessions to match date and team if you’re unsure which workout this is.',
      },
    ];
  }, [dashboardSessionId, archiveSessionForDialog]);

  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, hasSubItems: true, path: '/coach' },
    {
      id: 'teams',
      label: teamNav.navLabel,
      icon: Users2,
      hasSubItems: true,
      path: '/coach/teams',
    },
    { id: 'sessions', label: 'Sessions', icon: List, hasSubItems: true, path: '/coach/sessions' },
    { id: 'roster', label: 'Roster', icon: Users, hasSubItems: true, path: '/coach/roster' },
    {
      id: 'prototypes',
      label: 'Prototypes',
      icon: FlaskConical,
      hasSubItems: true,
      path: '/coach/prototypes',
    },
  ];

  const menuItems = allMenuItems.filter((item) => item.id !== 'prototypes' || showPrototypes);
  const dashboardItems = dashboards.map((dashboard) => ({
    id: dashboard.id,
    name: dashboard.name,
  }));

  const builderModeParams = useMemo(() => {
    if (location.pathname !== '/coach' && location.pathname !== '/coach/') {
      return { isBuilder: false, editId: null as string | null };
    }
    const params = new URLSearchParams(location.search);
    return {
      isBuilder: params.get('mode') === 'builder',
      editId: params.get('edit'),
    };
  }, [location.pathname, location.search]);

  const isDraftNavActive = (editingDashboardId: string | null) => {
    if (!builderModeParams.isBuilder) return false;
    if (editingDashboardId == null) {
      return builderModeParams.editId == null;
    }
    return builderModeParams.editId === editingDashboardId;
  };

  const formatDraftSavedAt = (savedAt: number) =>
    new Date(savedAt).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const recentSessions = useMemo(() => {
    const visible = sessions.filter(
      (session) => coachPreferences.showDemoContent || !session.isDemo
    );
    const byId = new Map(visible.map((session) => [session.id, session]));
    const fromVisits = recentSessionIds
      .map((id) => byId.get(id))
      .filter((session): session is (typeof visible)[number] => session != null)
      .slice(0, 3);

    const fallback = [...visible]
      .sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
      )
      .slice(0, 3);

    const source = fromVisits.length > 0 ? fromVisits : fallback;
    return source.map((session) => ({
      id: session.id,
      name: session.sessionName || `Session ${session.sessionId || session.id}`,
    }));
  }, [sessions, recentSessionIds, coachPreferences.showDemoContent]);

  const prototypeGroups = useMemo(() => {
    const groups: Record<string, PrototypeNavItem[]> = {};
    getPrototypesForPortal(UserRole.Coach).forEach((entry) => {
      const navItem: PrototypeNavItem = {
        id: entry.id,
        title: entry.title,
        platform: entry.platform,
      };
      if (!groups[navItem.platform]) groups[navItem.platform] = [];
      groups[navItem.platform].push(navItem);
    });
    return groups;
  }, []);
  const [expandedPrototypeGroups, setExpandedPrototypeGroups] = useState<Set<string>>(
    new Set(['D3.js', 'Three.js', 'Other'])
  );

  const navSignature = `${location.pathname}${location.search}`;
  const drillPanelForLocation = useMemo(
    () => sidebarDrillPanelForLocation(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const prevNavSignatureRef = useRef(navSignature);

  useEffect(() => {
    if (!sidebarDrill) return;
    return sidebarDrill.registerDrillHandler((panelId) => {
      if (!isCollapsed) setActiveDrillPanel(panelId);
    });
  }, [sidebarDrill, isCollapsed]);

  // Re-drill when the user opens a sub-item (route/search changes), not when they tap drill-back.
  useEffect(() => {
    if (isCollapsed) return;

    const prevSignature = prevNavSignatureRef.current;
    const navChanged = navSignature !== prevSignature;
    prevNavSignatureRef.current = navSignature;

    if (navChanged && drillPanelForLocation) {
      setActiveDrillPanel(drillPanelForLocation);
      return;
    }

    if (
      navChanged &&
      !drillPanelForLocation &&
      activeDrillPanel &&
      activeDrillPanel !== activeTab
    ) {
      setActiveDrillPanel(null);
    }
  }, [isCollapsed, navSignature, drillPanelForLocation, activeTab, activeDrillPanel]);

  const handleMenuItemClick = (itemId: string, hasSubItems: boolean) => {
    onTabChange(itemId);
    if (!isCollapsed && hasSubItems) {
      setActiveDrillPanel((prev) => (prev === itemId ? null : itemId));
    }
  };

  const activePanelMeta = menuItems.find((item) => item.id === activeDrillPanel) || null;
  const drillBackClass =
    'relative w-full flex items-center justify-center p-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-gray-100 transition-colors';
  const drillItemClass =
    'w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700';
  const drillItemActiveClass = 'bg-gray-100 text-gray-900 font-medium';

  const handleArchiveConfirm = async () => {
    if (!dashboardSessionId) return;
    setArchiveError(null);
    const result = await archiveSessions([dashboardSessionId]);
    if (result && 'success' in result && result.success) {
      setShowArchiveDialog(false);
      if (
        selectedSessionStorageKey &&
        sessionStorage.getItem(selectedSessionStorageKey) === dashboardSessionId
      ) {
        sessionStorage.removeItem(selectedSessionStorageKey);
      }
      navigate('/coach/sessions');
    }
  };

  return (
    <>
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={onToggle}
        navContent={
          <SidebarDrillTransition
            drillKey={!isCollapsed && activePanelMeta ? activeDrillPanel : null}
            drillMenu={
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setActiveDrillPanel(null)}
                  className={drillBackClass}
                >
                  <ChevronLeft className="absolute left-3 h-4 w-4" aria-hidden />
                  <span>{activePanelMeta?.label ?? ''}</span>
                </button>

                {activeDrillPanel === 'dashboard' && (
                  <ul className="space-y-1">
                    <li>
                      <button
                        onClick={() => onTabChange('dashboard')}
                        className={`${drillItemClass} ${
                          !currentDashboardId ? drillItemActiveClass : 'text-gray-700'
                        }`}
                      >
                        Starter Dashboard
                      </button>
                    </li>
                    {dashboardItems.map((dashboard) => (
                      <li key={dashboard.id}>
                        <button
                          onClick={() => onTabChange('dashboard', dashboard.id)}
                          className={`${drillItemClass} ${
                            currentDashboardId === dashboard.id
                              ? drillItemActiveClass
                              : 'text-gray-700'
                          }`}
                        >
                          {dashboard.name}
                        </button>
                      </li>
                    ))}
                    {builderDrafts.length > 0 ? (
                      <>
                        <li className="my-2 border-t border-gray-200"></li>
                        <li className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Drafts
                        </li>
                        {builderDrafts.map((draft) => {
                          const navSubItem = draftNavigationSubItem(draft.editingDashboardId);
                          const isActive = isDraftNavActive(draft.editingDashboardId);
                          return (
                            <li key={navSubItem}>
                              <button
                                type="button"
                                onClick={() => onTabChange('dashboard', navSubItem)}
                                className={`${drillItemClass} w-full ${
                                  isActive ? drillItemActiveClass : 'text-gray-700'
                                }`}
                                title={formatDraftSavedAt(draft.savedAt)}
                              >
                                <span className="block truncate font-medium text-gray-900">
                                  {draft.dashboardName}
                                </span>
                                <span className="block truncate text-xs text-gray-500">
                                  {draft.editingDashboardId
                                    ? `Edit · ${formatDraftSavedAt(draft.savedAt)}`
                                    : `New · ${formatDraftSavedAt(draft.savedAt)}`}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </>
                    ) : null}
                    <li className="my-2 border-t border-gray-200"></li>
                    <li>
                      <button
                        onClick={() => onTabChange('dashboard', 'create-new')}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-surface-muted hover:text-accent transition-colors text-green-600 font-semibold"
                      >
                        + Create New
                      </button>
                    </li>
                  </ul>
                )}

                {activeDrillPanel === 'teams' && (
                  <ul className="space-y-1">
                    <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                      {teamNav.recentSectionLabel}
                    </li>
                    {recentTeams.length === 0 ? (
                      <li className="px-3 py-1 text-xs text-gray-500">No teams visited yet</li>
                    ) : (
                      recentTeams.map((team) => (
                        <li key={team.id}>
                          <button
                            onClick={() => navigate(`/coach/teams/${team.id}`)}
                            className={drillItemClass}
                          >
                            {team.name}
                          </button>
                        </li>
                      ))
                    )}
                    <li className="my-2 border-t border-gray-200"></li>
                    <li>
                      <button
                        onClick={() =>
                          navigate('/coach/teams', {
                            state: { openTeamPicker: true },
                          })
                        }
                        className={drillItemClass}
                      >
                        {teamNav.membershipsActionLabel}
                      </button>
                    </li>
                  </ul>
                )}

                {activeDrillPanel === 'sessions' && (
                  <ul className="space-y-1">
                    <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                      Recent Sessions
                    </li>
                    {recentSessions.map((session) => (
                      <li key={session.id}>
                        <button
                          onClick={() => {
                            if (currentDashboardId) {
                              navigate(
                                `/coach?mode=custom&dashboard=${currentDashboardId}&session=${session.id}`
                              );
                            } else {
                              navigate(`/coach?session=${session.id}`);
                            }
                          }}
                          className={drillItemClass}
                        >
                          {session.name}
                        </button>
                      </li>
                    ))}
                    <li className="my-2 border-t border-gray-200"></li>
                    <li>
                      <button
                        type="button"
                        disabled={!dashboardSessionId || isArchiving}
                        onClick={() => {
                          setArchiveError(null);
                          setShowArchiveDialog(true);
                        }}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-amber-50 hover:text-amber-900 transition-colors text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Archive size={14} className="inline mr-1.5 align-text-bottom" />
                        Archive current session
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => navigate('/coach/sessions/archived')}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg ${drillItemClass}`}
                      >
                        Archived sessions
                      </button>
                    </li>
                    <li className="my-2 border-t border-gray-200"></li>
                    <li>
                      <button
                        type="button"
                        onClick={() => navigate('/coach/upload')}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-surface-muted hover:text-accent transition-colors text-green-600 font-semibold"
                      >
                        + Upload Session
                      </button>
                    </li>
                  </ul>
                )}

                {activeDrillPanel === 'roster' && (
                  <ul className="space-y-1">
                    <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                      Recent Rowers
                    </li>
                    {recentRowers.map((rower) => (
                      <li key={rower.id}>
                        <button
                          onClick={() => {
                            navigate(`/coach/rower/${rower.id}/journey`, {
                              state: {
                                from: `${location.pathname}${location.search}${location.hash}`,
                              },
                            });
                          }}
                          className={drillItemClass}
                        >
                          {rower.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {activeDrillPanel === 'prototypes' && (
                  <ul className="space-y-1">
                    <li>
                      <button
                        onClick={() => navigate('/coach/prototypes')}
                        className={`${drillItemClass} ${
                          location.pathname === '/coach/prototypes' ? drillItemActiveClass : ''
                        }`}
                      >
                        Prototypes Home
                      </button>
                    </li>
                    <li className="my-2 border-t border-gray-200"></li>
                    {Object.entries(prototypeGroups).map(([platform, entries]) => {
                      const isExpanded = expandedPrototypeGroups.has(platform);
                      return (
                        <li key={platform} className="space-y-1">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedPrototypeGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(platform)) next.delete(platform);
                                else next.add(platform);
                                return next;
                              })
                            }
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 hover:border-rowing-blue hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex items-center text-sm font-medium text-gray-800">
                              {isExpanded ? (
                                <ChevronRight className="h-4 w-4 mr-1 text-gray-500 rotate-90" />
                              ) : (
                                <ChevronRight className="h-4 w-4 mr-1 text-gray-500" />
                              )}
                              {platform}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {entries.length}
                            </span>
                          </button>
                          {isExpanded && (
                            <ul className="space-y-1 pl-2">
                              {entries.map((entry) => (
                                <li key={entry.id}>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/coach/prototypes/${entry.id}`)}
                                    className={`${drillItemClass} ${
                                      activePrototypeId === entry.id ? drillItemActiveClass : ''
                                    }`}
                                  >
                                    {entry.title}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            }
            rootMenu={
              <ul className="space-y-1">
                {menuItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => handleMenuItemClick(item.id, item.hasSubItems)}
                      className={`w-full flex items-center p-3 rounded-lg transition-colors relative ${
                        activeTab === item.id
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="font-medium ml-3">{item.label}</span>
                          {item.hasSubItems && (
                            <ChevronRight
                              className={`h-4 w-4 ml-auto transition-transform ${
                                activeDrillPanel === item.id ? 'rotate-90' : ''
                              }`}
                            />
                          )}
                        </>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            }
          />
        }
        footerContent={
          <div className={`px-3 ${isCollapsed ? 'pt-2 pb-0' : 'pt-3 pb-0'}`}>
            <button
              type="button"
              onClick={() => onTabChange('upload')}
              className={`w-full flex items-center justify-center ${uiBtn('outline', { size: 'sm' })} ${
                isCollapsed ? 'h-9 rounded-full' : 'h-10 rounded-xl'
              }`}
              aria-label="Upload"
            >
              <Upload className={`h-4 w-4 ${isCollapsed ? '' : 'mr-2'}`} />
              {!isCollapsed && <span className="font-medium">Upload</span>}
            </button>
            <div className="mt-1.5 border-t border-border pt-1">
              <SidebarUtilityStrip
                isCollapsed={isCollapsed}
                persona="coach"
                notificationsPath="/coach/notifications"
                inboxPath="/coach/inbox"
                settingsPath="/coach/settings"
              />
            </div>
          </div>
        }
        profileContent={
          <SidebarProfileMenu
            isCollapsed={isCollapsed}
            displayName={profileFullName || user?.user_metadata?.full_name || 'Coach'}
            email={user?.email || 'coach@glyyd.com'}
            profilePath="/coach/settings"
            onSignOut={() => signOut()}
            avatar={
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground text-sm font-bold">
                {user?.email ? user.email.charAt(0).toUpperCase() : 'C'}
              </div>
            }
            belowMenu={
              !isCollapsed ? (
                <Link
                  to="/pricing"
                  className={`block w-full text-center ${uiBtn('outline', { size: 'sm' })} rounded-full`}
                >
                  Upgrade
                </Link>
              ) : (
                <Link
                  to="/pricing"
                  className="auth-link-muted block px-1 py-0.5 text-center text-[10px]"
                  title="Upgrade"
                >
                  Pro
                </Link>
              )
            }
          />
        }
      />
      <SessionsArchiveDialog
        isOpen={showArchiveDialog}
        sessionName={archiveTargetName}
        detailRows={archiveDetailRows}
        isArchiving={isArchiving}
        error={archiveError}
        onConfirm={handleArchiveConfirm}
        onCancel={() => {
          setShowArchiveDialog(false);
          setArchiveError(null);
        }}
      />
    </>
  );
};

export default CoachSidebar;
